/**
 * /app/customer-ai — Owner dashboard for Customer AI (FASE 15).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson } from "@/lib/sm-api";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { CUSTOMER_LEXICON, customerEmptyState } from "@/lib/renata/customer/experience";
import { MessageCircle, Users, UserPlus, Handshake, ShoppingBag, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/app/customer-ai")({
  component: CustomerAiDashboardPage,
  head: () => ({ meta: [{ title: "Customer AI — Renata" }] }),
});

interface Dash {
  conversations: number;
  activeCustomers: number;
  leads: number;
  handoffs: number;
  openConversations: number;
  ratingsUp: number;
  ratingsDown: number;
  ventasAsistidas: number;
  bundlesVendidos: number;
  voice: number;
  eventsByType: Record<string, number>;
}

function CustomerAiDashboardPage() {
  const tenantId = useWorkspaceTenantId();
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [data, setData] = useState<Dash | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      try {
        const result = await apiGetJson<Dash>("/api/renata/customer-ai");
        setData(result);
      } catch (e) {
        const err = e as Error & { planLock?: PlanApiErrorPayload };
        if (err.planLock) { setLock(err.planLock); return; }
        setError(e instanceof Error ? e.message : "Error");
      }
    })();
  }, [tenantId]);

  const empty = customerEmptyState();

  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <RenataPageChrome screenId="customer-ai" title={CUSTOMER_LEXICON.home} subtitle="Conversaciones con clientes finales">
      <div className="space-y-6 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold">{CUSTOMER_LEXICON.home}</h1>
            <p className="text-sm text-muted-foreground">
              Misma Renata, superficie pública. Sin Insights ni herramientas internas.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/app/renata/handoff">Ver handoff</Link>
          </Button>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {!data && !error && (
          <p className="text-sm text-muted-foreground">Cargando métricas…</p>
        )}

        {data && data.conversations === 0 && (
          <div className="rounded-xl border border-dashed p-6 text-center">
            <p className="font-medium">{empty.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{empty.body}</p>
          </div>
        )}

        {data && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={MessageCircle} label="Conversaciones" value={data.conversations} />
            <Kpi icon={Users} label="Clientes activos" value={data.activeCustomers} />
            <Kpi icon={UserPlus} label="Prospectos" value={data.leads} />
            <Kpi icon={Handshake} label="Atención humana" value={data.handoffs} />
            <Kpi icon={ShoppingBag} label="Ventas asistidas" value={data.ventasAsistidas} />
            <Kpi icon={ShoppingBag} label="Bundles vendidos" value={data.bundlesVendidos} />
            <Kpi icon={Clock} label="Abiertas (24h)" value={data.openConversations} />
            <Kpi
              icon={MessageCircle}
              label="Satisfacción"
              value={`${data.ratingsUp}↑ / ${data.ratingsDown}↓`}
            />
          </div>
        )}

        <section className="rounded-xl border p-4">
          <h2 className="text-sm font-medium">Canales</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Storefront, widget, PWA, API activos. WhatsApp outbound disponible; Messenger,
            Instagram, Telegram y Email preparados (inbound en siguientes fases).
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <Link className="underline" to="/app/renata/chat" search={{} as never}>
              Chat interno (Owner)
            </Link>
            <span className="text-muted-foreground">·</span>
            <Link className="underline" to="/app/renata" search={{} as never}>
              Inicio Renata
            </Link>
          </div>
        </section>
      </div>
    </RenataPageChrome>
  );
}

function Kpi({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-xl border bg-card p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
